import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.png';

const SideNav: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, logout, user } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleNav = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    if (logout) {
      await logout();
      setIsOpen(false);
      navigate('/');
    }
  };

  const menuItems = [
    {
      icon: HomeIcon,
      label: 'Главная',
      path: '/',
      show: true
    },
    {
      icon: DocumentTextIcon,
      label: 'Мои заявки',
      path: '/my-applications',
      show: isAuthenticated
    }
  ];

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={toggleNav}
        className={`fixed top-4 left-4 z-50 p-3 rounded-xl bg-white shadow-lg hover:shadow-xl text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
          isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-label="Открыть меню"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleNav}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full bg-white shadow-2xl w-80 transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out z-40`}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center space-x-3"
              onClick={() => setIsOpen(false)}
            >
              <img src={logo} alt="Logo" className="h-8 w-auto" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Get-Goods.ai
              </span>
            </Link>
            <button
              onClick={toggleNav}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* User Info */}
        {isAuthenticated && user && (
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <UserCircleIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {user.email || 'Пользователь'}
                </div>
                <div className="text-sm text-gray-600">
                  {user.email || 'Аккаунт активен'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-6 flex-1">
          <div className="space-y-2">
            {menuItems
              .filter(item => item.show)
              .map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
          </div>

          {/* Telegram Support */}
          {isAuthenticated && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <a
                href="https://t.me/getgoodsai_bot"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
                <span>Telegram Поддержка</span>
              </a>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span className="font-medium">Выйти</span>
            </button>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <UserCircleIcon className="h-5 w-5" />
              <span className="font-medium">Войти</span>
            </Link>
          )}
          
          <div className="mt-4 text-center">
            <div className="text-xs text-gray-500">
              © 2024 Get-Goods.ai
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideNav;
